import { Component, ojs, app, v, state, component } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    monetary,
    fireEvent,
    showActionButton,
    eParse,
    cartEmpty,
    HIDDEN_PLAN_FEATURES,
} from "../../helpers";
import { PAGES, uc, api } from "../../declarations";
import { tool } from "../../fotastart";
import { $e } from "../../ojs.events";
import DeliveryAddress from "../DeliveryAddress";
import Swal from "sweetalert2";

const h = app("h");
ojs(DeliveryAddress);

// Default the schedule date/time inputs to right now, local time, so the
// customer doesn't have to pick "today" before also picking the time.
const getNowDateTimeParts = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return { date, time };
};

export default class Checkout extends Component {
    trialModalHolder = null;
    actionButtonShown = false;

    deliveryCostLoading = state(false);
    deliveryInfo = state(null);
    shouldSchedule = state(false);
    initialized = false;

    explorePlans() {
        fireEvent($e.subscription.needs.plans);
    }

    /**
     * Animated empty state for when no delivery company can take the order.
     * `no_coverage`         -> Carata doesn't operate in this area yet.
     * `no_deliverer_online` -> area is covered, but nobody is online right now.
     */
    renderNoDeliveryState(availability) {
        if (!document.getElementById("checkout-no-delivery-styles")) {
            const style = document.createElement("style");
            style.id = "checkout-no-delivery-styles";
            style.textContent = `
                @keyframes cnd-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
                @keyframes cnd-pulse { 0% { transform: scale(1); opacity: .5; } 100% { transform: scale(1.7); opacity: 0; } }
                @keyframes cnd-blink { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
                .cnd-icon-wrap { position: relative; width: 72px; height: 72px; display: flex; align-items: center; justify-content: center; }
                .cnd-icon-wrap::before { content: ""; position: absolute; inset: 0; border-radius: 50%; background: currentColor; opacity: .15; animation: cnd-pulse 2s ease-out infinite; }
                .cnd-icon { font-size: 2.2rem; animation: cnd-float 2.6s ease-in-out infinite; }
                .cnd-dot { width: 10px; height: 10px; border-radius: 50%; background: #dc3545; display: inline-block; animation: cnd-blink 1.4s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) { .cnd-icon, .cnd-icon-wrap::before, .cnd-dot { animation: none; } }
            `;
            document.head.appendChild(style);
        }

        const noCoverage = availability?.reason === "no_coverage";
        const message =
            availability?.message ||
            (noCoverage
                ? "Carata doesn't deliver to this area yet. We'll let you know once we do."
                : "No deliverers are online in your area right now. Please try again later.");

        return h.div(
            {
                class: "d-flex flex-column align-items-center text-center py-4 px-2 gap-2",
                role: "status",
            },
            h.div(
                {
                    class: `cnd-icon-wrap ${noCoverage ? "text-secondary" : "text-warning"}`,
                },
                h.i({
                    class: `cnd-icon bi ${noCoverage ? "bi-geo-alt" : "bi-bicycle"}`,
                }),
            ),
            h.div(
                { class: "d-flex align-items-center gap-2 fw-semibold small" },
                ...(noCoverage ? [] : [h.span({ class: "cnd-dot" })]),
                noCoverage ? "Not available here yet" : "No deliverers online",
            ),
            h.p({ class: "text-muted small mb-0" }, message),
        );
    }

    async fetchDeliveryPrice() {
        const addresses = uc.addresses.value;
        if (!addresses || addresses.length === 0) return;

        if (cartEmpty()) return;

        const activeAddress = addresses[addresses.length - 1];
        if (!activeAddress || !activeAddress.id) return;

        if (this.deliveryCostLoading.value === true) return;

        this.deliveryCostLoading.value = true;

        try {
            const res = await api.get(
                `/orders/delivery-price/d/${activeAddress.id}`,
                { scheduled: this.shouldSchedule.value ? 1 : 0 },
            );
            if (res && res.data) {
                this.deliveryInfo.value = res.data;

                // Select the first company
                const companies = res.data.companies;
                const companyKeys = Object.keys(companies);
                if (companyKeys.length > 0) {
                    const firstCompany = companies[companyKeys[0]];
                    uc.logisticsCompany.value = firstCompany;

                    // Automatically choose a default fleet type from available ones
                    const fleetTypes = firstCompany.fleet_types || [];
                    if (fleetTypes.length > 0) {
                        uc.fleetType.value = fleetTypes[0].name;
                    } else if (firstCompany.monetary_fee_per_vehicle) {
                        const vehicles = Object.keys(
                            firstCompany.monetary_fee_per_vehicle,
                        );
                        if (vehicles.length > 0) {
                            uc.fleetType.value = vehicles[0];
                        }
                    }
                }
            }
        } catch (e) {
            console.error("Error fetching delivery price:", e);
        } finally {
            this.deliveryCostLoading.value = false;
        }
    }

    showTrialUsageInfo() {
        if (this.trialModalHolder) {
            this.trialModalHolder.hide();
        }

        const sub = uc.subscription.value;
        if (!sub || !sub.id) return;

        let options = new tool.ModalOptions();
        options.buttons.right.visible = false;

        // Calculate days used
        const totalMs = new Date(sub.expires_at) - new Date(sub.started_at);
        const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
        const elapsedMs = new Date() - new Date(sub.started_at);
        const usedDays = Math.max(
            0,
            Math.ceil(elapsedMs / (1000 * 60 * 60 * 24)),
        );
        const displayUsedDays = Math.min(usedDays, totalDays);
        const durationPct =
            totalDays > 0 ? (displayUsedDays / totalDays) * 100 : 0;

        const progressCards = [];

        // 1. Duration Progress (if starts_at/expires_at exist)
        progressCards.push(
            h.div(
                { class: "col-12" },
                h.div(
                    { class: "p-3 bg-light rounded-3 text-center" },
                    h.small(
                        {
                            class: "text-muted d-block mb-1 text-uppercase fw-semibold",
                        },
                        "Duration (Days Used)",
                    ),
                    h.div(
                        { class: "fw-bold text-dark font-monospace" },
                        `${displayUsedDays} / ${totalDays} Days`,
                    ),
                    h.div(
                        { class: "progress mt-2", style: "height: 6px;" },
                        h.div({
                            class: "progress-bar",
                            style: `background-color: #0dcaf0; width: ${durationPct}%;`,
                        }),
                    ),
                ),
            ),
        );

        // Map feature names to user-friendly titles, progress colors, and labels
        const featureConfig = {
            number_of_orders: {
                title: "Completed Orders",
                color: "#ffc107",
                suffix: "Orders",
            },
            ai_credits: {
                title: "AI Credits",
                color: "#973e87",
                suffix: "Credits",
            },
            virtual_distance: {
                title: "Prepaid Delivery (vKM)",
                color: "#5d5fef",
                suffix: "vKM",
            },
        };

        if (sub.usages && Array.isArray(sub.usages)) {
            sub.usages.forEach((usage) => {
                const config = featureConfig[usage.feature_name];
                if (!config || Number(usage.limit) <= 0) return;
                if (HIDDEN_PLAN_FEATURES.includes(usage.feature_name)) return;

                const usedVal = Number(usage.used);
                const limitVal = Number(usage.limit);
                const pct = limitVal > 0 ? (usedVal / limitVal) * 100 : 0;

                progressCards.push(
                    h.div(
                        { class: "col-12" },
                        h.div(
                            { class: "p-3 bg-light rounded-3 text-center" },
                            h.small(
                                {
                                    class: "text-muted d-block mb-1 text-uppercase fw-semibold",
                                },
                                config.title,
                            ),
                            h.div(
                                { class: "fw-bold text-dark font-monospace" },
                                `${usedVal} / ${limitVal} ${config.suffix}`,
                            ),
                            h.div(
                                {
                                    class: "progress mt-2",
                                    style: "height: 6px;",
                                },
                                h.div({
                                    class: "progress-bar",
                                    style: `background-color: ${config.color}; width: ${pct}%;`,
                                }),
                            ),
                        ),
                    ),
                );
            });
        }

        let modalBody = h.div(
            { class: "p-2 text-dark" },
            h.p(
                { class: "text-muted small mb-4" },
                `Track your current usage relative to the subscription allowance for ${sub.plan?.name || "your plan"}.`,
            ),
            h.div({ class: "row g-3" }, ...progressCards),
        );

        let modal = tool.modal("Subscription Usage Stats", modalBody, options);
        this.trialModalHolder = modal.modal.object;
        this.trialModalHolder.show();
    }

    render(...args) {
        pageInfo(PAGES.CHECKOUT);
        scrollToTop();

        return h.main(
            { class: "checkout-page pb-5 pt-3 py-md-5 container mb-5" },
            h.div(
                { class: "row g-4" },
                h.div(
                    { class: "col-12 col-lg-8 d-flex flex-column gap-3" },

                    // 1. Delivery Address Card
                    h.DeliveryAddress(uc.addresses),

                    // 2. Subscription Guarded Section
                    v(uc.subscription, (sub) => {
                        const isSubscribed = sub.value && sub.value.id;

                        return h.div(
                            { class: "d-flex flex-column gap-3" },

                            // Order Scheduling Card - open to everyone, no subscription
                            // required. Placed above the Delivery Cost card since toggling
                            // it changes that fee.
                            h.div(
                                { class: "card border-0 shadow-sm rounded-4 p-4 bg-white" },
                                h.div(
                                    { class: "d-flex align-items-start gap-3" },
                                    h.div(
                                        {
                                            class: "position-relative d-flex align-items-center justify-content-center circle bg-primary-subtle text-primary flex-shrink-0",
                                            style: "width: 46px; height: 46px; font-size: 1.35rem;",
                                        },
                                        h.i({ class: "bi bi-calendar3" }),
                                        h.i({
                                            class: "bi bi-clock-fill position-absolute circle text-primary",
                                            style: "font-size: 0.65rem; bottom: -3px; right: -3px; background: #fff; --size: 20px; padding: 1px;",
                                        }),
                                    ),
                                    h.div(
                                        { class: "flex-grow-1" },
                                        h.h6({ class: "fw-bold text-primary mb-1" }, "Schedule For Later"),
                                        h.p(
                                            { class: "text-muted small mb-2" },
                                            "If you don't need this now, you can schedule it for later.",
                                        ),
                                        h.div(
                                            { class: "form-check form-switch mb-0" },
                                            h.input({
                                                class: "form-check-input",
                                                type: "checkbox",
                                                role: "switch",
                                                id: "schedule-order-switch",
                                                onchange: this.method("toggleSchedule", "${this}"),
                                            }),
                                            h.label(
                                                { class: "form-check-label small fw-bold text-secondary", for: "schedule-order-switch" },
                                                "Placed later",
                                            ),
                                        ),
                                        v(this.shouldSchedule, (scheduled) => {
                                            if (!scheduled.value) return null;
                                            return h.div(
                                                { class: "row g-3 mt-2 pt-2 border-top" },
                                                h.div(
                                                    { class: "col-md-6" },
                                                    h.label({ class: "form-label small fw-bold text-secondary" }, "Placement Date"),
                                                    h.input({
                                                        type: "date",
                                                        class: "form-control border-0 bg-light rounded-3 p-3",
                                                        oninput: this.method("setScheduleDate", "${this}"),
                                                        value: uc.scheduleDate.value || "",
                                                    }),
                                                    h.p({ class: "form-text small text-muted" }, "Placed on this date.")
                                                ),
                                                h.div(
                                                    { class: "col-md-6" },
                                                    h.label({ class: "form-label small fw-bold text-secondary" }, "Placement Time"),
                                                    h.input({
                                                        type: "time",
                                                        class: "form-control border-0 bg-light rounded-3 p-3",
                                                        min: "08:00",
                                                        max: "23:59",
                                                        oninput: this.method("setScheduleTime", "${this}"),
                                                        value: uc.scheduleTime.value || "",
                                                    }),
                                                    h.p({ class: "form-text small text-muted" }, "Placed at this time.")
                                                ),
                                            );
                                        }),
                                    ),
                                ),
                            ),

                            // Virtual Delivery Distance Card
                            v(uc.addresses, (addresses) => {
                                if (
                                    !addresses.value ||
                                    addresses.value.length === 0
                                ) {
                                    return null;
                                }
                                return h.div(
                                    {
                                        class: "card border-0 shadow-sm rounded-4 p-4 bg-white",
                                    },
                                    h.h6(
                                        {
                                            class: "fw-bold text-muted text-uppercase small mb-3 d-flex align-items-center gap-2",
                                        },
                                        h.i({ class: "bi bi-truck" }),
                                        "Delivery Cost",
                                    ),
                                    v(this.deliveryCostLoading, (loading) => {
                                        if (loading.value) {
                                            return h.div(
                                                {
                                                    class: "d-flex flex-column align-items-center justify-content-center py-4",
                                                },
                                                h.div({
                                                    class: "spinner-border text-primary mb-2",
                                                    role: "status",
                                                    style: "color: #5d5fef;",
                                                }),
                                                h.span(
                                                    {
                                                        class: "text-muted small",
                                                    },
                                                    "Calculating route and fees...",
                                                ),
                                            );
                                        }

                                        return v(this.deliveryInfo, (info) => {
                                            if (!info || !info.companies) {
                                                return h.div(
                                                    {
                                                        class: "text-center text-muted py-3 small",
                                                    },
                                                    "No delivery options available for this address.",
                                                );
                                            }

                                            // Get first company
                                            const companies = info.companies;
                                            const companyKeys =
                                                Object.keys(companies);
                                            if (companyKeys.length === 0) {
                                                return this.renderNoDeliveryState(
                                                    info.availability,
                                                );
                                            }

                                            const firstCompany =
                                                companies[companyKeys[0]];
                                            const fleet =
                                                uc.fleetType.value ||
                                                Object.keys(
                                                    firstCompany.monetary_fee_per_vehicle,
                                                )[0];

                                            const fee =
                                                firstCompany
                                                    .monetary_fee_per_vehicle[
                                                    fleet
                                                ] || 0;
                                            const vkmRequired =
                                                firstCompany
                                                    .vkm_required_per_vehicle?.[
                                                    fleet
                                                ] || 0;
                                            const paymentType =
                                                firstCompany
                                                    .payment_type_per_vehicle?.[
                                                    fleet
                                                ] || "monetary";
                                            const savings =
                                                firstCompany
                                                    .savings_per_vehicle?.[
                                                    fleet
                                                ] || fee;

                                            const getProvision = (
                                                subVal,
                                                key,
                                                defaultVal = 0,
                                            ) => {
                                                const subProv =
                                                    subVal?.provisions?.find(
                                                        (p) => p.key === key,
                                                    );
                                                if (subProv)
                                                    return Number(
                                                        subProv.value,
                                                    );
                                                const planProv =
                                                    subVal?.plan?.provisions?.find(
                                                        (p) => p.key === key,
                                                    );
                                                if (planProv)
                                                    return Number(
                                                        planProv.value,
                                                    );
                                                return defaultVal;
                                            };

                                            const minPurchase = getProvision(
                                                sub.value,
                                                "minimal_purchase_size",
                                                0,
                                            );

                                            const maxItemsLimit = getProvision(
                                                sub.value,
                                                "maximum_items_per_order",
                                                0,
                                            );

                                            const itemsCount = uc.cart.value ? (
                                                Array.isArray(uc.cart.value)
                                                    ? uc.cart.value.length
                                                    : Object.keys(uc.cart.value).length
                                            ) : 0;

                                            let cartTotal = 0;
                                            if (uc.cart.value) {
                                                const items = Array.isArray(
                                                    uc.cart.value,
                                                )
                                                    ? uc.cart.value
                                                    : Object.values(
                                                          uc.cart.value,
                                                      );
                                                items.forEach((item) => {
                                                    cartTotal +=
                                                        Number(item.qty) *
                                                        Number(item.price);
                                                });
                                            }

                                            const vkmUsage =
                                                sub.value?.usages?.find(
                                                    (u) =>
                                                        u.feature_name ===
                                                        "virtual_distance",
                                                );
                                            const currentVkm = vkmUsage
                                                ? Math.max(
                                                      0,
                                                      Number(vkmUsage.limit) -
                                                          Number(vkmUsage.used),
                                                  )
                                                : 0;

                                            return h.div(
                                                {
                                                    class: "d-flex flex-column gap-3",
                                                },
                                                 isSubscribed 
                                                     ? h.div(
                                                           {
                                                               class: "row g-3 align-items-center",
                                                           },
                                                           h.div(
                                                               {
                                                                   class: "col-6 text-center border-end",
                                                               },
                                                               h.span(
                                                                   {
                                                                       class: "text-muted small d-block mb-1",
                                                                   },
                                                                   "vKM Required",
                                                               ),
                                                               h.h3(
                                                                   {
                                                                       class: "fw-bold text-dark mb-0 font-monospace",
                                                                   },
                                                                   `${vkmRequired} vKM`,
                                                               ),
                                                           ),
                                                           h.div(
                                                               {
                                                                   class: "col-6 text-center",
                                                               },
                                                               h.span(
                                                                   {
                                                                       class: "text-muted small d-block mb-1",
                                                                   },
                                                                   "Current Available",
                                                               ),
                                                               h.h3(
                                                                   {
                                                                       class: "fw-bold text-primary mb-0 font-monospace",
                                                                       style: "color: #5d5fef;",
                                                                   },
                                                                   `${currentVkm.toFixed(1)} vKM`,
                                                               ),
                                                           ),
                                                       )
                                                     : h.div(
                                                           { class: "d-flex justify-content-between align-items-center text-start" },
                                                           h.span({ class: "text-muted fw-semibold" }, "Fee"),
                                                           h.h4({ class: "fw-bold text-dark mb-0 font-monospace" }, `KES ${Number(fee).toLocaleString()}`)
                                                       ),

                                                // Alert banner based on payment type
                                                h.call(() => {
                                                    if (!isSubscribed) {
                                                        return h.div(
                                                            {
                                                                class: "card border-0 rounded-4 p-4 text-center cursor-pointer hover-scale transition-all animated-cta-card",
                                                                onclick: this.method("explorePlans")
                                                            },
                                                            h.style(`
                                                                @keyframes gradientShift {
                                                                    0% { background-position: 0% 50%; }
                                                                    50% { background-position: 100% 50%; }
                                                                    100% { background-position: 0% 50%; }
                                                                }
                                                                .animated-cta-card {
                                                                    background: linear-gradient(-45deg, #5d5fef, #8247e9, #4f46e5, #6366f1);
                                                                    background-size: 300% 300%;
                                                                    animation: gradientShift 8s ease infinite;
                                                                    border: none !important;
                                                                    box-shadow: 0 8px 24px rgba(93, 95, 239, 0.25) !important;
                                                                }
                                                                .animated-cta-card:hover {
                                                                    transform: translateY(-3px) scale(1.01) !important;
                                                                    box-shadow: 0 12px 30px rgba(93, 95, 239, 0.4) !important;
                                                                }
                                                            `),
                                                            h.div(
                                                                { class: "d-flex flex-column align-items-center gap-2" },
                                                                h.i({ class: "bi bi-gem text-white fs-3 mb-1" }),
                                                                h.h6(
                                                                    { class: "fw-extrabold text-white mb-1 fs-5" },
                                                                    `Save KES ${Number(savings).toLocaleString()} Before You Checkout!`
                                                                ),
                                                                h.p(
                                                                    { class: "text-white text-opacity-90 small mb-0", style: "font-size: 0.9rem; line-height: 1.5;" },
                                                                    `Skip paying the KES ${Number(fee).toLocaleString()} delivery fee.`,
                                                                    h.br(),
                                                                    h.span(
                                                                        { class: "fw-bold text-white text-decoration-underline d-inline-block mt-2 fs-6", style: "letter-spacing: 0.5px;" },
                                                                        "Subscribe →"
                                                                    )
                                                                )
                                                            )
                                                        );
                                                    }

                                                    switch (paymentType) {
                                                        case "virtual_distance":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-success border-0 p-3 d-flex gap-3 align-items-center mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-check-circle-fill fs-4 text-success",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Paid with vKM",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "small mb-0 lh-base",
                                                                        },
                                                                        `Your subscription covers this delivery completely. 0 KES delivery fee will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_small_cart":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-warning border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-triangle-fill fs-4 text-warning",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Minimum Order Size Not Met",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `Reach KES ${minPurchase.toLocaleString()} to use your vKM. You're currently at KES ${cartTotal.toLocaleString()}.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_out_of_radius":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-danger border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-octagon-fill fs-4 text-danger",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Out of Delivery Radius",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `One or more items in your cart exceed your subscription's delivery radius. A delivery fee of KES ${fee} will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_items_limit":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-danger border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-octagon-fill fs-4 text-danger",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Order Items Limit Exceeded",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `Your cart has ${itemsCount} items, which exceeds your subscription's maximum items limit of ${maxItemsLimit} items. A delivery fee of KES ${fee} will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        default:
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-info border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-info-circle-fill fs-4 text-info",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Delivery Fee Charged",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `You do not have enough vKM remaining to cover this delivery. You will pay a delivery fee of KES ${fee}.`,
                                                                    ),
                                                                ),
                                                            );
                                                    }
                                                }),
                                            );
                                        });
                                    }),
                                );
                            }),

                            // Trial/Subscription Progress Card
                            isSubscribed
                                ? h.div(
                                      {
                                          class: "card border-0 shadow-sm rounded-4 p-4 bg-white",
                                          style: "border: 1px solid rgba(93, 95, 239, 0.08)!important;",
                                      },
                                      h.div(
                                          {
                                              class: "d-flex flex-column gap-3 text-start",
                                          },
                                          h.div(
                                              {
                                                  class: "d-flex justify-content-between align-items-center flex-wrap gap-2",
                                              },
                                              h.div(
                                                  {
                                                      class: "d-flex gap-2 flex-column",
                                                  },
                                                  h.span(
                                                      {
                                                          class: `rounded fw-bold py-1 px-3 ${
                                                              sub.value.trial_days > 0
                                                                  ? "bg-info bg-opacity-10 text-info border-info border-opacity-15"
                                                                  : "bg-success bg-opacity-10 text-success border-success border-opacity-15"
                                                          }`,
                                                          style: "font-size: 0.72rem;",
                                                      },
                                                      sub.value.trial_days > 0
                                                          ? "Active Trial"
                                                          : "Active Subscription",
                                                  ),
                                                  h.h6(
                                                      {
                                                          class: "fw-bold mb-0 text-dark",
                                                      },
                                                      sub.value.plan?.name ||
                                                          "Premium Plan",
                                                  ),
                                              ),
                                              h.div(
                                                  {
                                                      class: "d-flex gap-2 align-items-center",
                                                  },
                                                  h.button(
                                                      {
                                                          class: "btn btn-sm rounded-pill px-3 fw-bold",
                                                          onclick:
                                                              this.method(
                                                                  "explorePlans",
                                                              ),
                                                      },

                                                      sub.value.plan?.slug ===
                                                          "basic-plan"
                                                          ? "Upgrade Plan"
                                                          : "Change Plan",
                                                      sub.value.plan?.slug ===
                                                          "basic-plan"
                                                          ? {
                                                                class: "btn-outline-primary",
                                                            }
                                                          : {
                                                                class: "text-primary",
                                                            },
                                                  ),
                                                  h.button(
                                                      {
                                                          class: "btn btn-link text-muted p-1 border-0 d-flex align-items-center justify-content-center",
                                                          onclick:
                                                              this.method(
                                                                  "showTrialUsageInfo",
                                                              ),
                                                      },
                                                      h.i({
                                                          class: "bi bi-info-circle fs-5",
                                                      }),
                                                  ),
                                              ),
                                          ),
                                          // Custom Upgrade Banner / Message
                                          sub.value.plan?.slug === "basic-plan"
                                              ? h.div(
                                                    {
                                                        class: "p-3 rounded-3 mt-1 d-flex gap-2 align-items-start text-start",
                                                        style: "background-color: rgba(93, 95, 239, 0.05); border: 1px dashed rgba(93, 95, 239, 0.2);",
                                                    },
                                                    h.i({
                                                        class: "bi bi-stars text-primary fs-5 mt-0.5",
                                                        style: "color: #5d5fef;",
                                                    }),
                                                    h.div(
                                                        h.strong(
                                                            {
                                                                class: "d-block text-dark small mb-0.5",
                                                            },
                                                            "Get the best from Carata!",
                                                        ),
                                                        h.p(
                                                            {
                                                                class: "text-secondary small mb-0",
                                                                style: "font-size: 0.82rem; line-height: 1.4;",
                                                            },
                                                            "Upgrade your plan for more prepaid delivery (vKM) and more deliveries at the same time.",
                                                        ),
                                                    ),
                                                )
                                              : null,
                                      ),
                                  )
                                : null,

                            // Notes Card
                            h.Card(
                                {
                                    body: h.div(
                                        {
                                            data_intro:
                                                "Add any additional information that the deliverer should consider",
                                            data_step: 2,
                                        },
                                        h.label(
                                            {
                                                class: "form-label small fw-bold text-secondary",
                                                for: "order-notes",
                                            },
                                            "Additional Notes (optional)",
                                        ),
                                        h.textarea({
                                            class: "form-control border-0 bg-light rounded-3 p-3 text-secondary border-focus-primary",
                                            id: "order-notes",
                                            rows: "3",
                                            placeholder:
                                                "Describe specific delivery landmarks or requests...",
                                            oninput: this.method(
                                                "saveOrderNotes",
                                                "${this}",
                                            ),
                                        }),
                                    ),
                                },
                                {
                                    class: "border-0 shadow-sm rounded-4 bg-white",
                                },
                            ),

                            // Place Order Button
                            v(uc.cart, (cart) => {
                                let payable = 0;
                                if (cart && cart.value) {
                                    const items = Array.isArray(cart.value)
                                        ? cart.value
                                        : Object.values(cart.value);
                                    items.forEach((item) => {
                                        payable +=
                                            Number(item.qty) *
                                            Number(item.price);
                                    });
                                }

                                return v(this.deliveryInfo, (info) => {
                                    let deliveryFee = 0;
                                    if (info && info.companies) {
                                        const companyKeys = Object.keys(
                                            info.companies,
                                        );
                                        if (companyKeys.length > 0) {
                                            const firstCompany =
                                                info.companies[companyKeys[0]];
                                            const fleet =
                                                uc.fleetType.value ||
                                                Object.keys(
                                                    firstCompany.monetary_fee_per_vehicle,
                                                )[0];
                                            const paymentType =
                                                firstCompany
                                                    .payment_type_per_vehicle?.[
                                                    fleet
                                                ] || "monetary";
                                            if (
                                                paymentType !==
                                                "virtual_distance"
                                            ) {
                                                deliveryFee =
                                                    firstCompany
                                                        .monetary_fee_per_vehicle[
                                                        fleet
                                                    ] || 0;
                                            }
                                        }
                                    }

                                    const total = payable + deliveryFee;
                                    return h.button(
                                        {
                                            class: "btn btn-dark btn-lg w-100 rounded-pill py-3 fw-bold shadow-sm mb-4",
                                            onclick: this.method("placeOrder"),
                                        },
                                        `Place Order and Pay KES ${total.toLocaleString()}`,
                                    );
                                });
                            }),
                        );
                    }),
                ),
            ),
            ...args,
        );
    }

    saveOrderNotes(elem) {
        uc.orderNotes = elem.value;
    }

    toggleSchedule(elem) {
        // Set the date/time defaults BEFORE flipping shouldSchedule - that
        // assignment is what triggers the v(this.shouldSchedule, ...) block
        // to re-render the date/time inputs, reading uc.scheduleDate/Time
        // at that exact moment. Doing it in the other order (as this used
        // to) re-rendered the inputs first - while they were still empty -
        // then set the "now" defaults a moment too late to show up, since
        // nothing re-renders those inputs again afterward.
        if (!elem.checked) {
            uc.scheduleDate.value = "";
            uc.scheduleTime.value = "";
        } else if (!uc.scheduleDate.value || !uc.scheduleTime.value) {
            const { date, time } = getNowDateTimeParts();
            uc.scheduleDate.value = uc.scheduleDate.value || date;
            uc.scheduleTime.value = uc.scheduleTime.value || time;
        }

        this.shouldSchedule.value = elem.checked;

        // Scheduling carries its own flat fee, so the delivery cost shown
        // (and what will actually be charged) needs to be recalculated as
        // soon as the customer toggles it.
        this.fetchDeliveryPrice();
    }

    setScheduleDate(elem) {
        uc.scheduleDate.value = elem.value;
    }

    setScheduleTime(elem) {
        uc.scheduleTime.value = elem.value;
    }

    placeOrder() {
        if (this.shouldSchedule.value) {
            if (!uc.scheduleDate.value || !uc.scheduleTime.value) {
                Swal.fire({
                    icon: 'error',
                    title: 'Scheduling Required',
                    text: 'Please select both date and time to schedule your order.'
                });
                return;
            }
        }
        fireEvent($e.order.needs.placement);
    }

    $_mounted(componentId) {
        let c = component(componentId);

        if (!c) return;

        if (!c.initialized) {
            c.deliveryCostLoading.listener(c);
            c.deliveryInfo.listener(c);

            // Fetch new delivery prices whenever the addresses list or selected address changes
            uc.addresses.listener(() => {
                c.fetchDeliveryPrice();
            });

            uc.subscription.listener(() => {
                c.fetchDeliveryPrice();
            });

            app("broker").send($e.subscription.needs.active);

            c.initialized = true;
        }

        c.fetchDeliveryPrice();
    }
}

ojs(Checkout);
